import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useSearchParams } from "react-router-dom";
import moment from 'moment';
import axios from 'axios';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import PopupLayout from "layouts/PopupLayout";
import { getCommunity, authUserPermission } from 'helpers/community';
import Paginator from 'components/Paginator';
import ThSortable from "components/ThSortable";
import TrSkeleton from "components/TrSkeleton";
import { jsonToArray } from 'helpers/generic';
import { appendToSession, getFromSession } from 'helpers/session';

const Table = styled.table`
	th,td {
		&:nth-child(1) {
		}

		&:nth-child(2) {
			width: 100px;
			text-align: center;

			small {
				font-size: 10px;
			}
		}

		&:nth-child(3) {
			width: 150px;
		}

		&:nth-child(4) {
			width: 160px;
		}

		&:nth-child(5) {
			width: 50px;
		}
	}

	td {
		&:nth-child(2) {
			line-height: 12px;
		}
	}

	span[class*="status-"] {
		padding: 5px;
		user-select: none;

		&.status-draft {
			background: var(--bs-gray-200);
		}

		&.status-printed {
			background: var(--bs-success);
			color: white;
		}
	}
`;

let axiosCancelToken = null;
let searchTimeout = null;

const RECIPIENTS_TYPES = {
	owners: 'Propietarios',
	renters: 'Inquilinos',
	providers: 'Proveedores',
	clients: 'Clientes',
	contacts: 'Contactos'
};

const STATUSES = {
	draft: 'Borrador',
	printed: 'Impreso'
};

export default function PostalMails() {
	const [queryParams] = useSearchParams();

	let tableBodyRef = useRef(null);

	let filters = getFromSession('postal-mails-list');

	let [forceReload, setForceReload] = useState(null);
	let [postalMails, setPostalMails] = useState({});
	let [search, setSearch] = useState(filters?.search ?? undefined);
	let [recipientsType, setRecipientsType] = useState(filters?.recipients_type ?? undefined);
	let [status, setStatus] = useState(filters?.status ?? undefined);
	let [sortDirection, setSortDirection] = useState(filters?.direction ?? 'desc');
	let [sortField, setSortField] = useState(filters?.sort ?? 'created_at');
	let [detailsOpened, setDetailsOpened] = useState(filters?.detailsOpened ?? []);
	let [skeletonRows, setSkeletonRows] = useState(5);
	let [page, _setPage] = useState(queryParams.get('page') ?? (filters?.page ?? 1));
	const setPage = (page) => {
		setPostalMails({...postalMails, data: undefined});
		_setPage(page);
	}

	const setSearchTimeout = (value) => {
		if ( searchTimeout ) clearTimeout(searchTimeout);
		searchTimeout = setTimeout(() => {
			setSearch(value);
			setPage(1);
		}, 1000);
	}

	const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};

	const toggleDetails = (rowId) => {
		if ( detailsOpened.includes(rowId) ) {
			detailsOpened.splice(detailsOpened.indexOf(rowId), 1);
		} else {
			detailsOpened = [...detailsOpened, rowId];
		}

		setDetailsOpened([...detailsOpened]);
		appendToSession('postal-mails-list', {detailsOpened: detailsOpened});
	}

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		const getPostalMails = () => {
			let params = {
				community_id: getCommunity()?.id,
				page: page,
				search: search,
				sort: sortField,
				direction: sortDirection,
				recipients_type: recipientsType,
				status: status
			};

			appendToSession('postal-mails-list', params);

			axios.get('/api/manager/postal-mails/list', {
				params: params,
				cancelToken: axiosCancelToken.token
			}).then((response) => {
		    	setPostalMails({...response.data});
		    	setSkeletonRows(response.data.data.length);
		  	}).catch((error) => {
				if ( axios.isCancel(error) ) return;
			});
		}
		getPostalMails();
	}, [forceReload, page, search, sortField, sortDirection, recipientsType, status]);

	const deletePostalMail = (postalMail) => {
		const c = window.confirm('¿Quieres eliminar esta carta?');
		if ( !c ) return;

		axios.post('/api/manager/postal-mails/delete/' + postalMail.id, {
			community_id: postalMail.community_id
		}, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
	    	toast.info('Carta borrada')
	    	setForceReload(Math.random());
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});
	}

	const printPostalMail = (postalMail) => {
		axios.post('/api/manager/postal-mails/print/' + postalMail.id, {
			community_id: postalMail.community_id
		}, {
			responseType: 'blob',
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			// Crear URL del blob y descargar
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'Correo Postal - ' + postalMail.subject + '.pdf');
			document.body.appendChild(link);
			link.click();
			link.remove();

	    	setForceReload(Math.random());
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
			toast.error('Error al generar el PDF');
		});
	}

	return (
		<PopupLayout>
			<section>
				<div className="page-title">
					<h1>Correo Postal</h1>
					{ authUserPermission('edit') &&
						<NavLink to="/postal-mails/add" className="btn btn-sm btn-light ms-auto">Nueva carta</NavLink>
					}
				</div>

				<div className="page-content">
					<div className="card border-0 shadow-sm">
						<div className="card-header bg-white p-3">
							<div className="row">
								<div className="col-md-3 mb-2 mt-2 mb-md-0 mt-md-0">
									<input type="search" className="form-control form-control-sm" placeholder="Buscar" defaultValue={search ?? ''} onChange={(e) => setSearchTimeout(e.target.value)} />
								</div>
								<div className="col-md-2 mb-2 mt-2 mb-md-0 mt-md-0">
									<select className="form-control form-control-sm" onChange={(e) => setRecipientsType(e.target.value)} value={recipientsType}>
										<option value="">- Destinatario -</option>
										{ jsonToArray(RECIPIENTS_TYPES).map((el, idx) => {
											return (
												<option key={idx} value={el.key}>{el.value}</option>
											)
										})}
									</select>
								</div>
								<div className="col-md-2 mb-2 mt-2 mb-md-0 mt-md-0">
									<select className="form-control form-control-sm" onChange={(e) => setStatus(e.target.value)} value={status}>
										<option value="">- Estado -</option>
										{ jsonToArray(STATUSES).map((el, idx) => {
											return (
												<option key={idx} value={el.key}>{el.value}</option>
											)
										})}
									</select>
								</div>
								<div className="col-md-2 mb-2 mt-2 mb-md-0 mt-md-0">

								</div>
							</div>
						</div>
						<div className="card-body p-0">
							<div className="table-responsive table-responsive-carded">
								<Table className="table table-hover table-sortable table-carded">
									<thead>
										<tr>
											<ThSortable direction={sortDirection} active={sortField === 'subject'} onClick={() => sortTableClick('subject')}>Asunto</ThSortable>
											<ThSortable direction={sortDirection} active={sortField === 'recipients.count'} onClick={() => sortTableClick('recipients.count')}>Destinatarios</ThSortable>
											<ThSortable direction={sortDirection} active={sortField === 'created_at'} onClick={() => sortTableClick('created_at')}>Creado el</ThSortable>
											<ThSortable direction={sortDirection} active={sortField === 'status'} onClick={() => sortTableClick('status')}>Estado</ThSortable>
											<th style={{width: '40px'}}></th>
										</tr>
									</thead>
									<tbody ref={tableBodyRef}>
										{ postalMails.data?.length > 0 &&
											postalMails.data.map((el, idx) => {
												return (
													<React.Fragment key={idx}>
														<tr
															className="has-details"
															onClick={() => toggleDetails(el.id)}
														>
															<td>{el.subject}</td>
															<td>
																<div>{el.recipients_ids?.length ?? 0}</div>
																<small>
																	{ RECIPIENTS_TYPES[el.recipients_type] }
																</small>
															</td>
															<td>{el.created_at ? moment(el.created_at).format('DD-MM-YYYY') : '-'}</td>
															<td><span className={'status-' + el.status}>{STATUSES[el.status]}</span></td>
															<td>
																<NavLink className="btn-table me-3" to={'/postal-mails/edit/' + el.id}><i className="bi bi-pencil"></i></NavLink>
																<NavLink
																	className="btn-table text-secondary"
																	to={'/postal-mails/add?copy_from_id=' + el.id}
																	onClick={(e) => {
																		if ( !window.confirm('¿Quieres copiar esta carta?') ) {
																			e.preventDefault();
																			e.stopPropagation();
																		}
																	}}
																>
																	<i className="bi bi-copy"></i>
																</NavLink>
															</td>
														</tr>
														{ detailsOpened.includes(el.id) &&
															<tr className="details">
																<td colSpan="100%">
																	<div className="row ms-0 me-0">
																		<div className="col-md-4">
																			<div><b className="sbold">Asunto</b>: {el.subject ?? '-'}</div>
																			<div>
																				<b className="sbold">Adjuntos</b>:&nbsp;
																				{el.attachments?.map((aEl, aIdx) => {
																					return (
																						<span className="badge bg-light text-secondary fw-normal me-1" key={aIdx}>{aEl.name}</span>
																					);
																				}) ?? '-'}
																			</div>
																			<div className="mb-3 sbold">
																				<b className="sbold">Mensaje</b>:
																				<div dangerouslySetInnerHTML={{__html: el.message ?? '-'}}></div>
																			</div>
																		</div>
																		<div className="col-md-6">
																			<div>
																				<b className="sbold">Tipo de destinatario</b>:&nbsp;
																				{ RECIPIENTS_TYPES[el.recipients_type] }
																			</div>
																			<div><b className="sbold">Creado el</b>: {el.created_at ? moment(el.created_at).format('DD-MM-YYYY HH:mm') : '-'}</div>
																			<div><b className="sbold">Estado</b>: <span className={'small status-' + el.status}>{STATUSES[el.status]}</span></div>
																		</div>
																<div className="col-md-2">
																	{ authUserPermission('edit') &&
																		<React.Fragment>
																			<NavLink to={'/postal-mails/edit/' + el.id} className="btn btn-sm btn-secondary text-white w-100 mb-2">Editar</NavLink>
																			<button
																				className="btn btn-sm btn-primary text-white w-100 mb-2"
																				onClick={() => printPostalMail(el)}
																				disabled={!el.recipients_ids || el.recipients_ids.length === 0}
																			>
																				Imprimir
																			</button>
																		</React.Fragment>
																	}
																	{ authUserPermission('delete') &&
																		<React.Fragment>
																			{ el.is_deletable && <button className="btn btn-sm btn-danger text-white w-100 mb-2" onClick={() => deletePostalMail(el)}>Eliminar</button> }
																		</React.Fragment>
																	}
																</div>
																		</div>
																</td>
															</tr>
														}
													</React.Fragment>
												);
											})
										}

										{ postalMails.data && !postalMails.data.length && <tr><td colSpan="100%">No hay datos disponibles</td></tr> }

										{ postalMails.data === undefined && <TrSkeleton rows={skeletonRows} columns={5} /> }
									</tbody>
								</Table>
							</div>
						</div>
						<div className="card-footer p-3 d-flex justify-content-end" id="footer-fixed">
							<div className="d-inline-block">
								<Paginator
									min={1}
									current={postalMails?.current_page}
									max={postalMails?.last_page}
									changeCallback={(page) => setPage(page)}
								/>
							</div>
						</div>
					</div>
				</div>
			</section>
		</PopupLayout>
	);
}

